import { ChatGptResponse } from './PromptGeneratorApiClient.types';
import { IPromptGeneratorApiClient } from './PromptGeneratorApiService.types';
import { AIProviderFactory, DEFAULT_MODEL_ID } from '../AIProviderService';
import { buildEnhancerSystemPrompt, VolumeLevel } from '../../config/enhancerPrompts';
import { enqueueGenerationJob, waitForGenerationResult } from '../AsyncGenerationService';
import { ISSUE_TYPE } from '../../screen/GenerationPanel/Generationpanel.types';

/**
 * PromptGeneratorApiClient
 * 
 * Handles prompt enhancement using the selected AI model.
 * Supports multi-model generation through AIProviderFactory.
 */
export class PromptGeneratorApiClient implements IPromptGeneratorApiClient {
  /**
   * Generate an enhanced prompt using the selected AI model
   * 
   * @param userNotes - The user's input to enhance
  * @param isChatGpt4 - Legacy parameter (ignored; modelId is authoritative)
   * @param modelId - Optional model ID to use (if not provided, uses default)
   * @param volume - Optional volume level for prompt customization
   * @param issueType - Optional issue type for context-aware enhancement
   * @returns The enhanced prompt response
   */
  async generateEnhancedPrompt(
    userNotes: string,
    _isChatGpt4: boolean,
    modelId?: string,
    volume?: VolumeLevel,
    issueType?: ISSUE_TYPE
  ): Promise<ChatGptResponse | undefined> {
    const resolvedModelId = modelId ?? DEFAULT_MODEL_ID;
    return this.generateEnhancedPromptWithModel(userNotes, resolvedModelId, volume, issueType);
  }

  /**
   * Generate enhanced prompt using the AIProviderFactory (new multi-model approach)
   */
  private async generateEnhancedPromptWithModel(
    userNotes: string,
    modelId: string,
    volume: VolumeLevel = 'medium',
    issueType?: ISSUE_TYPE
  ): Promise<ChatGptResponse | undefined> {
    try {
      const provider = AIProviderFactory.getProviderForModel(modelId);
      const modelConfig = provider.getAvailableModels().find(model => model.id === modelId);
      const systemPrompt = buildEnhancerSystemPrompt(volume, issueType);

      const { requestId } = await enqueueGenerationJob({
        provider: provider.provider,
        modelName: modelConfig?.name || modelId,
        messages: [{ role: 'user', content: userNotes }],
        systemPrompt: systemPrompt,
        temperature: 0,
        requestType: 'enhancer'
      });

      const response = await waitForGenerationResult(requestId);

      return {
        id: `enhanced-${Date.now()}`,
        object: 'chat.completion',
        created: Math.floor(Date.now() / 1000),
        model: response.model,
        usage: {
          prompt_tokens: response.usage.promptTokens,
          completion_tokens: response.usage.completionTokens,
          total_tokens: response.usage.totalTokens
        },
        choices: [{
          message: {
            role: 'assistant',
            content: response.content
          },
          finish_reason: response.finishReason,
          index: 0
        }]
      };
    } catch (error) {
      console.error('Error generating enhanced prompt with model:', error);
      throw error;
    }
  }
}

